'use client';

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { isForgeWebEnabled } from '@/lib/utils/env';
import { useTrackingWallets } from '@/contexts/TrackingWalletsProvider';
import MonitorPortfolio from './portfolio/MonitorPortfolio';
import CreateWallet from './portfolio/CreateWallet';
import PortfolioTrackRender from './portfolio/PortfolioTrackRender';
import ForgeDownloadPage from '@/app/predic/forge-download/page';

export default function PortfolioPage({ openInSidebar }: { openInSidebar?: boolean }) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  // isForgeWebEnabled 读 window/localStorage，需等挂载后再判，避免水合错位。
  useEffect(() => setMounted(true), []);

  // loading 已含账户加载态（Provider 内合并），空态只看手动追踪的钱包
  const { wallets, loading } = useTrackingWallets();
  const systemWallets = wallets.systemWallet;

  // 先看有没有系统钱包：有钱包说明用户已经开过户，Web 端照常放行，
  // 哪怕正式环境整体还没开放 Forge——不该把已有钱包的人赶去下载页。
  // 只有「没有钱包」时才看开放开关：正式环境 Web 端不提供开户流程，引导下载 App。
  //
  // 因此这里必须等 loading 结束才能判定：钱包还没拉回来就下结论的话，
  // 有钱包的用户同样会被误判成「没钱包」而看到下载页。
  const forgeWebBlocked = mounted && !loading && !systemWallets && !isForgeWebEnabled();

  // 挂载前不渲染任何内容，避免水合错位闪现
  if (!mounted) return null;

  if (loading && !systemWallets) {
    return (
      <div className="flex items-center justify-center py-30">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  // 就地渲染下载页，不做路由跳转：本组件也会被右侧抽屉直接挂载
  // （UserContextCard 里 openDrawer(<PortfolioPage openInSidebar />)），
  // 跳转会把整个页面导走，抽屉场景下等于把用户踢出当前上下文。
  if (forgeWebBlocked)
    return (
      <div className="-mx-6 -mt-15">
        <PortfolioTrackRender showLabel />
        <ForgeDownloadPage />
      </div>
    );

  if (!systemWallets) {
    return (
      <>
        <PortfolioTrackRender showLabel />
        <CreateWallet />
      </>
    );
  }

  return <MonitorPortfolio openInSidebar={openInSidebar} />;
}
